import useFormatter from "@/hooks/use-formatter.hook.ts";
import {
  ApiEsignResult,
  EsignEnvelopeStatus,
  EsignRequestStatus,
  EsignSignatureType,
} from "@/types/esign.types.ts";
import { Stack, Text, Tooltip, TooltipContent, TooltipTrigger } from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

export interface DocumentEsignStatusProps {
  esignResult: ApiEsignResult;
  onClick?: () => void;
}

export function DocumentEsignStatus({ esignResult, onClick }: DocumentEsignStatusProps) {
  const { t } = useTranslation();
  const { renderDate, renderDateAndTime } = useFormatter();

  const isSignature = esignResult.signature_type === EsignSignatureType.signature;
  const isCompleted = esignResult.status === EsignEnvelopeStatus.completed;
  const isCancelled = esignResult.status === EsignEnvelopeStatus.cancelled;

  const statusWord = isCompleted
    ? isSignature
      ? t("core:esign_status_signed")
      : t("core:esign_status_acknowledged")
    : isCancelled
      ? t("core:esign_status_cancelled")
      : t("core:esign_status_pending");

  const label =
    isCompleted && esignResult.datetime_completed_at
      ? `${statusWord} ${renderDate(esignResult.datetime_completed_at)}`
      : statusWord;

  const statusClass = isCompleted
    ? "text-positive"
    : isCancelled
      ? "text-secondary"
      : "text-warning";

  const signerStatusLabel = (status: EsignRequestStatus) =>
    status === EsignRequestStatus.signed
      ? isSignature
        ? t("core:esign_status_signed")
        : t("core:esign_status_acknowledged")
      : status === EsignRequestStatus.cancelled
        ? t("core:esign_status_cancelled")
        : status === EsignRequestStatus.declined
          ? t("core:esign_status_declined")
          : t("core:esign_status_pending");

  return (
    <Tooltip>
      <TooltipTrigger asChild={true}>
        <Text
          className={twMerge(
            statusClass,
            "text-base w-fit",
            onClick && "cursor-pointer hover:underline"
          )}
          onClick={onClick}
        >
          {label}
        </Text>
      </TooltipTrigger>
      <TooltipContent>
        <Stack gap="xs">
          {esignResult.signers.map((signer) => (
            <Text key={signer.request_public_id} className="text-sm">
              {signer.full_name} · {signerStatusLabel(signer.status)}
              {signer.datetime_signed_at ? `, ${renderDateAndTime(signer.datetime_signed_at)}` : ""}
            </Text>
          ))}
        </Stack>
      </TooltipContent>
    </Tooltip>
  );
}
